import React, { createRef, FunctionComponent, KeyboardEvent, MouseEventHandler, ReactNode, RefObject, useEffect } from "react";

import { bemClassNames, createQA, Key } from "@multiweb/reactUtils";
import { QAProps } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { FormComponentEventHandlersProps, FormComponentProps } from "../../types";
import { CheckBoxComponentSharedProps } from "../types";
import { getNextCheckBoxValue } from "./utils";

type CheckBoxSwitcherChildrenProps = {
    ref: RefObject<HTMLDivElement>;
};

export type CheckBoxSwitcherProps = {
    /** Potomci, kteří mají k dispozici referenci určenou pro vstupní pole checkboxu */
    children: (props: CheckBoxSwitcherChildrenProps) => ReactNode;
    /** Příznak, zda chceme vynutit v checkboxu hodnotu */
    forceDefaultValue?: boolean;
    /** Defaultní hodnota checkboxu při vynucení hodnoty */
    defaultValue?: boolean;
}
& Pick<FormComponentProps, "encrypted" | "disabled">
& Pick<FormComponentEventHandlersProps,
    | "onClick"
    | "onMouseOver"
    | "onMouseEnter"
    | "onMouseLeave"
    | "onKeyDown"
    | "onFocus"
    | "onBlur"
    >
& Omit<CheckBoxComponentSharedProps, "id" | "showFalseAsCrossed">
& QAProps;

const classNames = bemClassNames("check-box-switcher-component");

/** Obalovací komponenta zajišťující logiku pro dvoustavové a třístavové zaškrtávací pole */
export const CheckBoxSwitcher: FunctionComponent<CheckBoxSwitcherProps> = ({
    children,
    encrypted = false,
    hasThreeStates = false,
    onChange,
    onClick,
    onMouseOver,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    onFocus,
    onBlur,
    disabled = false,
    title,
    value,
    qa,
    forceDefaultValue = false,
    defaultValue = true,
}) => {
    const checkBoxFieldRef = createRef<HTMLDivElement>();

    const switchValue = () => {
        if (!disabled && !encrypted && onChange) {
            if (hasThreeStates) {
                onChange(getNextCheckBoxValue(value));
            } else {
                onChange(!value);
            }
        }
    };

    const handleClick: MouseEventHandler = (e) => {
        onClick && onClick(e);
        switchValue();
        if (!disabled && !encrypted && checkBoxFieldRef.current) {
            checkBoxFieldRef.current.focus();
        }
    };

    const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.key === Key.Space) {
            switchValue();
        }
    };

    useEffect(() => {
        if (forceDefaultValue && onChange)
            onChange(defaultValue);

        // Chceme provádět pouze při prvním renderu ([]), v ostatních případech
        // už nastavení hodnoty je necháno na samotné komponentě.
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            {...createQA(qa)}
            className={classNames({
                encrypted,
                disabled,
            })}
            role={Role.checkBox}
            aria-checked={!!value}
            onClick={handleClick}
            onKeyPress={handleKeyPress}
            {...{
                title,
                onFocus,
                onMouseOver,
                onMouseEnter,
                onMouseLeave,
                onKeyDown,
                onBlur,
            }}
        >
            {children({ ref: checkBoxFieldRef })}
        </div>
    );
};
